import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "../styles/components/OpenAIBot.css";
import omnicareLogo from "../assets/icons/logo.svg";
import LanguageSwitcher from "./LanguageSwitcher";
import { searchKnowledgeBase } from "../utils/ragSearch";
import { buildSystemPrompt } from "../data/systemPrompt";

const URGENT_KEYWORDS = [
  "chest pain", "heart attack", "can't breathe", "cannot breathe",
  "difficulty breathing", "severe bleeding", "stroke", "unconscious",
  "suicide", "kill myself", "overdose",
];

function isUrgent(text) {
  const lower = text.toLowerCase();
  return URGENT_KEYWORDS.some((kw) => lower.includes(kw));
}

const SUGGESTIONS = [
  "What cancer screenings do I need?",
  "How often should I check cholesterol?",
  "What are early signs of diabetes?",
  "Am I at risk for breast cancer?",
];

const SendIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="22" y1="2" x2="11" y2="13" />
    <polygon points="22 2 15 22 11 13 2 9 22 2" />
  </svg>
);

const OpenAIBot = () => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [statusText, setStatusText] = useState("");
  const scrollRef = useRef(null);
  const textareaRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, statusText]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 120) + "px";
    }
  }, [input]);

  const getMockResponse = (userMessage) => {
    const results = searchKnowledgeBase(userMessage, 3);
    if (results.length > 0) {
      // Build a conversational response from multiple chunks
      const allCitations = [];
      const points = results.map((r) => {
        r.chunk.citations.forEach((c) => {
          if (!allCitations.includes(c)) allCitations.push(c);
        });
        // Summarize each chunk into a digestible point
        return `• ${r.chunk.content.split('.').slice(0, 2).join('.')}.\n`;
      });

      const intro = results.length > 1
        ? "Based on current medical guidelines, here's what I found:\n\n"
        : "Here's what the evidence says:\n\n";

      return {
        text: `${intro}${points.join('\n')}\n⚕️ These are general guidelines — please talk to your doctor for advice specific to your situation.`,
        citations: allCitations,
      };
    }
    return {
      text: "I don't have specific information on that topic yet. I can help with:\n\n• Cancer screenings (breast, lung, colorectal, cervical, etc.)\n• Risk factors (smoking, alcohol, obesity, family history)\n• Diabetes, cholesterol, and blood pressure screening\n• Prevention strategies\n\nTry asking about one of these topics!",
      citations: [],
    };
  };

  const handleSuggestionClick = (suggestion) => {
    setInput(suggestion);
    // Small delay so user sees it in the input, then send
    setTimeout(() => {
      sendMessageWithContent(suggestion);
    }, 100);
  };

  const sendMessageWithContent = async (content) => {
    if (!content.trim() || isSending) return;
    setInput("");
    const userMsg = { role: "user", content: content.trim() };
    setMessages((prev) => [...prev, userMsg]);

    if (isUrgent(content)) {
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: "🚨 If you are experiencing a medical emergency, please call 911 (US) or your local emergency number immediately. Do not wait — go to the nearest emergency room.\n\nI'm not a substitute for emergency medical care.",
          citations: [],
        },
      ]);
      return;
    }

    setIsSending(true);
    setStatusText("Searching knowledge base...");

    try {
      const ragResults = searchKnowledgeBase(content, 3);
      const contextChunks = ragResults.map((r) => r.chunk);
      setStatusText("Thinking...");

      const apiKey = process.env.REACT_APP_OPENAI_API_KEY;
      if (!apiKey) {
        const mock = getMockResponse(content);
        setMessages((prev) => [...prev, { role: "assistant", content: mock.text, citations: mock.citations }]);
        return;
      }

      const systemPrompt = buildSystemPrompt(contextChunks);
      const apiMessages = [
        { role: "system", content: systemPrompt },
        ...messages.slice(-6).map((m) => ({ role: m.role, content: m.content })),
        { role: "user", content },
      ];

      const resp = await fetch("https://api.openai.com/v1/chat/completions", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${apiKey}` },
        body: JSON.stringify({ model: "gpt-4o-mini", messages: apiMessages, temperature: 0.3, max_tokens: 500 }),
      });

      if (!resp.ok) {
        if (resp.status === 429) {
          const mock = getMockResponse(content);
          setMessages((prev) => [...prev, { role: "assistant", content: mock.text, citations: mock.citations }]);
          return;
        }
        throw new Error(`HTTP ${resp.status}`);
      }

      const data = await resp.json();
      const assistantText = data.choices?.[0]?.message?.content?.trim() || "Thanks for your message!";
      const usedCitations = [...new Set(contextChunks.flatMap((c) => c.citations))];

      setMessages((prev) => [...prev, { role: "assistant", content: assistantText, citations: usedCitations }]);
    } catch (err) {
      const mock = getMockResponse(content);
      setMessages((prev) => [...prev, { role: "assistant", content: mock.text, citations: mock.citations }]);
    } finally {
      setIsSending(false);
      setStatusText("");
    }
  };

  const sendMessage = () => sendMessageWithContent(input);

  const handleKeyDown = (e) => {
    if ((e.key === "Enter" || e.keyCode === 13) && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const hasMessages = messages.length > 0;

  return (
    <div className="openai-bot-page">
      {/* Header */}
      <header className="bot-header">
        <div className="bot-header-left" onClick={() => navigate("/landing")} style={{ cursor: "pointer" }}>
          <div className="bot-header-avatar">
            <img src={omnicareLogo} alt="" style={{ filter: "brightness(10)" }} />
          </div>
          <div>
            <div className="bot-header-title">Pebble</div>
            <div className="bot-header-subtitle">Health Screening Assistant</div>
          </div>
        </div>
        <div className="bot-header-right">
          <LanguageSwitcher />
        </div>
      </header>

      {/* Messages */}
      <div className="chat-messages" ref={scrollRef}>
        {!hasMessages ? (
          <div className="chat-welcome">
            <div className="chat-welcome-icon">
              <span>🩺</span>
            </div>
            <h2>How can I help?</h2>
            <p>Ask me anything about health screenings, cancer prevention, risk factors, or next steps. I'll cite my sources.</p>
            <div className="chat-suggestions">
              {SUGGESTIONS.map((s, i) => (
                <button key={i} className="chat-suggestion-chip" onClick={() => handleSuggestionClick(s)}>
                  {s}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="chat-messages-inner">
            {messages.map((m, idx) => (
              <div key={idx} className={`chat-message ${m.role}`}>
                <div className="message-avatar">
                  {m.role === "assistant" ? "🩺" : "👤"}
                </div>
                <div className="message-content">
                  <div className="message-text">{m.content}</div>
                  {m.role === "assistant" && m.citations && m.citations.length > 0 && (
                    <div className="message-citations">
                      <strong>Sources:</strong> {m.citations.join(" · ")}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {statusText && (
              <div className="chat-message assistant">
                <div className="message-avatar">🩺</div>
                <div className="message-content">
                  <div className="message-status">
                    <div className="thinking-dots">
                      <span></span><span></span><span></span>
                    </div>
                    <span className="status-label">{statusText}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Input */}
      <div className="chat-input-area">
        <div className="chat-input-container">
          <div className="chat-input-box">
            <textarea
              ref={textareaRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask about screenings, risk factors, prevention..."
              rows={1}
            />
            <button
              className="chat-send-btn"
              onClick={sendMessage}
              disabled={isSending || !input.trim()}
            >
              <SendIcon />
            </button>
          </div>
          <div className="chat-disclaimer">
            This is not medical advice. Always consult your healthcare provider.
          </div>
        </div>
      </div>
    </div>
  );
};

export default OpenAIBot;
